<script setup>

import ModalDisclaimer from './pages/00m1-ModalDisclaimer'
import Home from './pages/01-Home.vue'
import ModalSingleCouple from './pages/01m1-ModalSingleCouple'
import ModalWhatInfo from './pages/01m2-ModalWhatInfo'
import CustomStandard from './pages/02-CustomStandard.vue'
import ModalSra from './pages/02m1-ModalSra'
import RetirementSavings from './pages/03-RetirementSavings.vue'
import ModalAddSaving from './pages/03m1-ModalAddSaving'
import MyResults from './pages/04-MyResults.vue'
import {computed, ref, watch, onBeforeMount} from "vue";
import {round10} from "@/math";
import {store} from "@/store";

const workingData = ref({});
const blankWorkingData = ref({
  standards: [],
  standardTotal: {
    title: 'Total income',
    value: 0,
    iconUrl: 'https://api.modeller.concertconsult.co.uk/storage/rebranded/icon/Icons_TotalIncome.svg',
    presetsSingle: { min: 0, mod: 0, com: 0 },
    presetsCouple: { min: 0, mod: 0, com: 0 }
  },
  standardsAnnualSummary: {
    single: { min: 0, mod: 0, com: 0 },
    couple: { min: 0, mod: 0, com: 0 }
  },
  savings: {
    state: { totalAnnual: 0, totalWeekly: 0, parts: [] },
    personal: { totalAnnual: 0, totalWeekly: 0, parts: [] },
    dc: { totalAnnual: 0, totalWeekly: 0, parts: [] },
    db: { totalAnnual: 0, totalWeekly: 0, parts: [] },
    other: { totalAnnual: 0, totalWeekly: 0, parts: [] },
  },
  savingsTotal: { annual: 0, weekly: 0 },
  user: { isCouple: false, yearOfBirth: false, annuityRate: false, sra: false, sraBeforeSpa: false }
});

const apiData = ref({
  category: [],
  summary: []
})
const dataLoaded = ref(false);

const constructWorkingData = function () {
  const newWorkingData = JSON.parse(JSON.stringify(blankWorkingData.value)); // Begin with a copy of the blank data structure
  // Parse API summary data into newWorkingData.standardsAnnualSummary object
  apiData.value.summary.forEach(function (type) {
    newWorkingData.standardsAnnualSummary[type.name.toLowerCase()] = {
      min: round10(type.minimum/100),
      mod: round10(type.moderate/100),
      com: round10(type.comfortable/100),
    }
  });
  // Parse API category data into newWorkingData.standards array
  apiData.value.category.forEach(function (superStandard, superIndex) {
    const presetsTarget = superStandard.type === 'Single' ? 'presetsSingle' : 'presetsCouple';
    superStandard.categories.forEach(function (category, index) {
      if (superIndex === 0) {
        newWorkingData.standards.push({
          title: category.title,
          value: 0,
          iconUrl: 'https://api.modeller.concertconsult.co.uk/storage/rebranded/' + category.icon,
          presetsSingle: { min: 0, mod: 0, com: 0 },
          presetsCouple: { min: 0, mod: 0, com: 0 }
        })
      }
      newWorkingData.standards[index][presetsTarget].min += round10(category.minimum/100);
      newWorkingData.standards[index][presetsTarget].mod += round10(category.moderate/100);
      newWorkingData.standards[index][presetsTarget].com += round10(category.comfortable/100);
    })
  });
  // Define starting values for calculated properties of standards derrived from API category data
  for (const standard of newWorkingData.standards) {
    standard.value = standard.presetsSingle.mod; // Set retirement standards default values equal to "moderate" preset
    newWorkingData.standardTotal.presetsSingle.min += standard.presetsSingle.min; // Rollups to accumulate retirement standard total presets
    newWorkingData.standardTotal.presetsSingle.mod += standard.presetsSingle.mod;
    newWorkingData.standardTotal.presetsSingle.com += standard.presetsSingle.com;
    newWorkingData.standardTotal.presetsCouple.min += standard.presetsCouple.min;
    newWorkingData.standardTotal.presetsCouple.mod += standard.presetsCouple.mod;
    newWorkingData.standardTotal.presetsCouple.com += standard.presetsCouple.com; // End rollups
  }
  newWorkingData.standardTotal.value = newWorkingData.standardTotal.presetsSingle.mod; // Set retirement standard total default value equal to "moderate" preset
  // Finish
  workingData.value = newWorkingData;
  store.initialiseFormData(newWorkingData);
}

const standardTotalValue = computed(() => {
  let totalValue = 0;
  if (dataLoaded.value) for (const standard of workingData.value.standards) totalValue += standard.value;
  return totalValue
})

const savingsTotalValue = computed(() => {
  let totalValue = { annual: 0, weekly: 0 };
  if (dataLoaded.value) for (const [key, saving] of Object.entries(workingData.value.savings)) {
    if (!(key === 'state' && workingData.value.user.sraBeforeSpa)) {
      totalValue.annual += saving.totalAnnual;
      totalValue.weekly += saving.totalWeekly;
    }
  }
  return totalValue

})


watch(standardTotalValue, (newValue) =>
{ workingData.value.standardTotal.value = newValue; }
)
watch(savingsTotalValue, (newValue) =>
{ workingData.value.savingsTotal = newValue })

onBeforeMount(async () => {
  const apiReqCategory = await axios.get(`https://api.modeller.concertconsult.co.uk/api/v1/category`),
      apiReqSummary = await axios.get(`https://api.modeller.concertconsult.co.uk/api/v1/summary`);
  apiData.value = {
    category: apiReqCategory.data,
    summary: apiReqSummary.data
  }
  constructWorkingData();
  dataLoaded.value = true;
})
</script>

<template>
  <div id="cc-2609-app">
    <div v-if="dataLoaded === true">

      <!-- Modal 0a -->
      <ModalDisclaimer
        v-if="store.formState.modal === 'disclaimer'"
      ></ModalDisclaimer>

      <!-- Page 1 -->
      <Home
        title="Retirement Living Standards Modeller"
        v-if="store.formState.page === 'home'"
      >
        <p>Welcome to the Retirement Living Standards Modeller. With this online tool you can:</p>
        <ul>
          <li>Explore the three different Retirement Living Standards set by the Pensions and Lifetime Savings Association (PLSA);</li>
          <li>Increase or decrease the assumption for one or more of the six expense categories to create your own personal Living Standard; and </li>
          <li>Input the values of your current retirement savings to assess whether you have enough to fulfil your retirement aspirations.</li>
        </ul>
        <h3>Who is the PLSA?</h3>
        <p>The PLSA brings together the pensions industry and other parties to raise standards, share best practice, and support its members. It represents over 1,300 pension schemes with 20 million members across all types of pension arrangements.</p>
        <h3>What are the PLSA Retirement Living Standards?</h3>
        <p>The Retirement Living Standards, based on independent research by Loughborough University, have been developed to help you to picture what kind of lifestyle you could have in retirement. You can find out more information <a href="https://www.retirementlivingstandards.org.uk/" target="_blank">here.</a></p>
        <p>To get started click the ‘Next’ button. You can also see a summary of each of the initial PLSA Standards by clicking on the images.</p>
      </Home>

      <!-- Modal 1a -->
      <ModalSingleCouple
        v-if="store.formState.modal === 'singleCouple'"
      ></ModalSingleCouple>
      
      <!-- Modal 1b -->
      <ModalWhatInfo
        v-if="store.formState.modal === 'whatInfo'"
      ></ModalWhatInfo>

      <!-- Page 2 -->
      <CustomStandard
        title="Create my own Retirement Living Standard"
        v-if="store.formState.page === 'customStandard'"
      >
        <p>Each of the six expense categories have been set to the PLSA ‘moderate’ Retirement Living Standard for a couple. You can adjust one or more of these to create your own personal Retirement Living Standard by either:</p>
        <ul>
          <li>Moving the relevant slider to increase or decrease the expense level for that category; or</li>
          <li>Typing the weekly amount you require for each expense category in the boxes to the right of the sliders.</li>
        </ul>
        <p>Once you are happy with the personal Retirement Living Standard you have created click ‘Next’ to start adding your retirement savings details.</p>
      </CustomStandard>

      <!-- Modal 2a -->
      <ModalSra
        v-if="store.formState.modal === 'sra'"
      ></ModalSra>

      <!-- Page 3 -->
      <RetirementSavings
        title="Add my retirement savings"
        v-if="store.formState.page === 'retirementSavings'"
      >
        <p>It’s now time to enter your current retirement savings.</p>
        <ul>
          <li>You can get an estimate of your State Pension <a href="https://www.gov.uk/check-state-pension" target="_blank">here.</a></li>
          <li>You will find an estimated value of your Personal Account on your latest Annual Benefit Statement. You can also log in to ‘<a href="https://dc.prudentialstaffps.co.uk/my-account" target="_blank">My account</a>’ and obtain an up to date estimate.</li>
        </ul>
        <p>You will also need the estimated value of any other defined contribution savings you have and the estimated pension you are likely to receive from any defined benefits savings you have. If you do not have these figures to hand you should contact the administrators of those pension arrangements and request a retirement quotation based on your <strong>Selected Retirement Age</strong>. If you do not have their contact details, you may be able to find these by using the Government’s ‘<a href="https://www.gov.uk/find-pension-contact-details" target="_blank">Find pension contact details</a>’ service. </p>
        <p>If you have any other savings you have put aside to fund your retirement (for example a property you plan to rent out) you can also add these.</p>
        <p>Don’t forget - if you plan to take some of your pension savings as a cash sum at retirement you should take this into account.</p>
        <ul>
          <li>For defined contribution arrangements simply deduct this amount from the estimated fund value. You can normally take up to 25% of the total value as a cash sum (which is normally tax-free).</li>
          <li>For defined benefit arrangements the amount of cash you can take and the resulting reduced pension is normally included in your retirement quotation.</li>
        </ul>
      </RetirementSavings>

      <!-- Modal 3a -->
      <ModalAddSaving
        v-if="store.formState.modal === 'addSaving'"
      ></ModalAddSaving>

      <!-- Page 4 -->
      <MyResults
        title="Your results"
        v-if="store.formState.page === 'myResults'"
        @reset="constructWorkingData"
      ></MyResults>
    </div>
  </div>
</template>


<style lang="scss">
  #cc-2609-app {
    $color-primary: #055a60;
    $color-secondary: #eb5c37;
    $pru-red: #055a60;
    $pru-green: #055a60;
    $pru-pink: #055a60; // #b0107d;
    $pru-purple: #eb5c37; //#45176b;
    $pru-purple-light: #baabc5;
    $pru-purple-lighter: #c6bed1;
    $pru-purple-extra-light: #ece9f0;
    $pru-grey-extra-light: #f1f2f2;
    $pru-grey-light: #8c8c8c;
    $pru-grey-med: #73787c;
    $pru-grey-dark: #565656;

    $gap: 30px;
    $stroke: 2px;
    overflow: hidden;
    font-size: 15px;
    color: $pru-grey-dark;

    $xs: 0;
    $sm: 576px;
    $md: 768px;
    $lg: 1100px;
    $xl: 1400px;

    @import './styles/vue-select-config.scss';
    @import './styles/range-input.scss';
    @import './styles/radio.scss';

    @import './styles/basestyles.scss';
    @import './styles/nav-bar.scss';
    @import './styles/modal.scss';
    @import './styles/fields-table.scss';
    @import './styles/savings-table.scss';
    @import './styles/standard-preset-cards.scss';
    @import './styles/standard-slider.scss';
    @import './styles/results-chart.scss';
    @import './styles/info-popup.scss';
  }
</style>
